import type { BaseSyntheticEvent } from 'react';
import { useEffect } from 'react';
import {
  useForm,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { useCreateEducation } from '../hooks/useCreateEducation';
import { useUpdateEducation } from '../hooks/useUpdateEducation';
import type { Education } from '../types/education.types';
import type { EducationFormData } from './EducationFormFields';
import {
  DEFAULT_VALUES,
  toFormValues,
  buildRequest,
  getLabels,
  resetFormState,
  submitCreate,
  submitUpdate,
} from './EducationFormDialog.utils';

export function useEducationFormHandlers(onOpenChange: (open: boolean) => void): {
  isLoading: boolean;
  handleClose: () => void;
  createEducation: ReturnType<typeof useCreateEducation>['createEducation'];
  updateEducation: ReturnType<typeof useUpdateEducation>['updateEducation'];
} {
  const { createEducation, isLoading: isCreating } = useCreateEducation();
  const { updateEducation, isLoading: isUpdating } = useUpdateEducation();

  return {
    isLoading: isCreating || isUpdating,
    handleClose: () => onOpenChange(false),
    createEducation,
    updateEducation,
  };
}

interface EducationFormStateParams {
  candidateId: string;
  mode: 'create' | 'edit';
  education?: Education | null;
  onOpenChange: (open: boolean) => void;
}

interface EducationFormState {
  register: UseFormRegister<EducationFormData>;
  errors: FieldErrors<EducationFormData>;
  setValue: UseFormSetValue<EducationFormData>;
  watch: UseFormWatch<EducationFormData>;
  onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  title: string;
  submitLabel: string;
  isLoading: boolean;
  handleClose: () => void;
}

export function useEducationFormState({
  candidateId,
  mode,
  education,
  onOpenChange,
}: EducationFormStateParams): EducationFormState {
  const handlers = useEducationFormHandlers(onOpenChange);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<EducationFormData>({
    defaultValues: mode === 'edit' && education ? toFormValues(education) : DEFAULT_VALUES,
  });

  useEffect(() => resetFormState(reset, mode, education), [education, mode, reset]);

  const onSubmit = handleSubmit((data) => {
    const request = buildRequest(data);
    if (mode === 'create') {
      submitCreate(handlers.createEducation, {
        candidateId,
        request,
        onDone: () => {
          reset(DEFAULT_VALUES);
          handlers.handleClose();
        },
      });
    } else if (mode === 'edit' && education) {
      submitUpdate(handlers.updateEducation, education.id, {
        candidateId,
        request,
        onDone: handlers.handleClose,
      });
    }
  });

  const { title, submitLabel } = getLabels(mode, handlers.isLoading);

  return {
    register,
    errors,
    setValue,
    watch,
    onSubmit,
    title,
    submitLabel,
    isLoading: handlers.isLoading,
    handleClose: handlers.handleClose,
  };
}
